import type { SeriesRef } from '../dicomweb';
import type { ViewerShell } from '../shell';
import { downloadBytes, elementBytes, fetchBulk, retrieveInstance } from './retrieve';

/**
 * Encapsulated PDF (SOP 1.2.840.10008.5.1.4.1.1.104.1, spec section 1.2): the Encapsulated Document
 * (0042,0011) is fetched through DICOMweb with the viewer ticket, turned into a blob URL and shown in
 * a frame with the A4 aspect ratio, plus a " Descargar PDF" button.
 */

export const PDF_TEXT = {
  download: ' Descargar PDF',
  frameTitle: 'Documento PDF',
  open: ' Abrir PDF',
  noInline: 'Este navegador no puede mostrar el PDF dentro del visor. Descárguelo o ábralo con el lector del dispositivo.',
} as const;
/** A4 height / width (IMEXHS pdfFrame). */
export const A4_ASPECT = 1.4142857;
const DOCUMENT_TAG = '00420011';
const DOCUMENT_LENGTH_TAG = '00420015';
const PDF_MIME = 'application/pdf';

/** Drops the pad byte DICOM adds to odd-length values, using Encapsulated Document Length when known. */
function trimDocument(bytes: Uint8Array, declaredLength: number | undefined): Uint8Array {
  if (declaredLength && declaredLength > 0 && declaredLength <= bytes.length) return bytes.subarray(0, declaredLength);
  let end = bytes.length;
  while (end > 0 && bytes[end - 1] === 0) end--;
  return bytes.subarray(0, end);
}

/** The PDF bytes: bulk data URI from the metadata when there is one, else the whole instance. */
export async function fetchPdf(shell: ViewerShell, series: SeriesRef): Promise<Uint8Array> {
  const instance = series.instances[0];
  const declared = Number(instance.metadata?.[DOCUMENT_LENGTH_TAG]?.Value?.[0]);
  try {
    const bulk = await fetchBulk(shell, instance.metadata, DOCUMENT_TAG);
    if (bulk && bulk.length > 0) return trimDocument(bulk, declared);
  } catch (error) {
    console.warn('[viewer] PDF bulk data unavailable, retrieving the instance', error);
  }
  const dataSet = await retrieveInstance(shell, series, instance);
  const bytes = elementBytes(dataSet, 'x00420011');
  if (!bytes) throw new Error('no Encapsulated Document');
  return trimDocument(bytes, dataSet.uint32('x00420015'));
}

/** Largest A4 box that fits the cell. */
function fitA4(host: HTMLElement, frame: HTMLElement, reserved: number): void {
  const width = host.clientWidth;
  const height = Math.max(0, host.clientHeight - reserved);
  const fitWidth = Math.min(width, height / A4_ASPECT);
  frame.style.width = `${Math.floor(fitWidth)}px`;
  frame.style.height = `${Math.floor(fitWidth * A4_ASPECT)}px`;
}

/**
 * Whether the browser renders PDFs in a frame. `navigator.pdfViewerEnabled` answers it where it
 * exists; older browsers without it: Android has no inline PDF viewer (the frame shows blank or an
 * "Open" prompt), desktop and iOS do.
 */
export function canShowPdfInline(nav: { pdfViewerEnabled?: boolean; userAgent?: string } = navigator): boolean {
  if (typeof nav.pdfViewerEnabled === 'boolean') return nav.pdfViewerEnabled;
  return !/Android/i.test(nav.userAgent ?? '');
}

/** Builds the PDF view in `host`; returns its cleanup (revokes the blob URL). */
export async function renderPdf(shell: ViewerShell, series: SeriesRef, host: HTMLDivElement): Promise<() => void> {
  const bytes = await fetchPdf(shell, series);
  return renderPdfBytes(host, bytes, `${series.seriesInstanceUID}.pdf`);
}

/** The PDF view (A4 frame + " Descargar PDF") for bytes already in hand, e.g. the study report. */
export function renderPdfBytes(host: HTMLElement, bytes: Uint8Array, filename: string): () => void {
  const url = URL.createObjectURL(new Blob([bytes], { type: PDF_MIME }));
  if (!canShowPdfInline()) return renderPdfFallback(host, bytes, filename, url);
  const frame = document.createElement('iframe');
  frame.className = 'pdf-frame';
  frame.title = PDF_TEXT.frameTitle;
  frame.src = url;
  frame.dataset.bytes = String(bytes.length);
  const bar = document.createElement('div');
  bar.className = 'display-actions';
  const download = document.createElement('button');
  download.type = 'button';
  download.className = 'display-button pdf-download';
  download.textContent = PDF_TEXT.download;
  download.addEventListener('click', () => downloadBytes(bytes, PDF_MIME, filename));
  bar.appendChild(download);
  host.append(frame, bar);
  const observer = new ResizeObserver(() => fitA4(host, frame, bar.offsetHeight));
  observer.observe(host);
  fitA4(host, frame, bar.offsetHeight);
  return () => {
    observer.disconnect();
    frame.remove();
    bar.remove();
    URL.revokeObjectURL(url);
  };
}

/** No inline viewer: say so, and make download (and the device's own reader) the way in. */
function renderPdfFallback(host: HTMLElement, bytes: Uint8Array, filename: string, url: string): () => void {
  const box = document.createElement('div');
  box.className = 'pdf-no-inline';
  box.dataset.bytes = String(bytes.length);
  const message = document.createElement('p');
  message.textContent = PDF_TEXT.noInline;
  const bar = document.createElement('div');
  bar.className = 'display-actions';
  const download = document.createElement('button');
  download.type = 'button';
  download.className = 'display-button pdf-download';
  download.textContent = PDF_TEXT.download;
  download.addEventListener('click', () => downloadBytes(bytes, PDF_MIME, filename));
  const open = document.createElement('a');
  open.className = 'display-button pdf-open';
  open.href = url;
  open.target = '_blank';
  open.rel = 'noopener';
  open.textContent = PDF_TEXT.open;
  bar.append(download, open);
  box.append(message, bar);
  host.append(box);
  return () => {
    box.remove();
    URL.revokeObjectURL(url);
  };
}
